import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Workflow, Plus } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth-store';
import { WorkflowCard } from '@/features/workflows/components/WorkflowCard';
import { StepExecutionPanel } from '@/features/workflows/components/StepExecutionPanel';

interface WorkflowInstance {
  id: string;
  title: string;
  template_name: string;
  current_step: number;
  status: 'todo' | 'in_progress' | 'review' | 'done';
  due_date: string | null;
  step_count: number;
  completed_steps: number;
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  step_count: number;
}

const KANBAN_COLUMNS: { key: WorkflowInstance['status']; label: string }[] = [
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
];

export function WorkflowsPage() {
  const profile = useAuthStore((s) => s.profile);
  const isAdmin = profile?.role === 'admin';
  const queryClient = useQueryClient();

  const [showStart, setShowStart] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<WorkflowInstance | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newTemplateId, setNewTemplateId] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [tplName, setTplName] = useState('');
  const [tplDescription, setTplDescription] = useState('');

  const { data: instances, isLoading, error, refetch } = useQuery({
    queryKey: ['workflow-instances'],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('workflow_instance')
        .select('*')
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      const statusMap: Record<string, WorkflowInstance['status']> = {
        active: 'in_progress', completed: 'done', cancelled: 'done',
      };
      return (data ?? []).map((d) => ({
        id: d.id,
        title: d.title,
        template_name: '',
        current_step: d.current_step,
        status: statusMap[d.status] ?? 'todo',
        due_date: d.due_date,
        step_count: 0,
        completed_steps: Math.max(0, d.current_step - 1),
      })) as unknown as WorkflowInstance[];
    },
  });

  const { data: templates } = useQuery({
    queryKey: ['workflow-templates'],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('workflow_template')
        .select('*')
        .order('name');
      if (fetchError) throw fetchError;
      return (data ?? []).map((d) => ({
        id: d.id,
        name: d.name,
        description: d.description,
        step_count: Array.isArray(d.steps) ? d.steps.length : 0,
      })) as WorkflowTemplate[];
    },
  });

  const createTemplate = useMutation({
    mutationFn: async () => {
      const { error: insertError } = await supabase.from('workflow_template').insert({
        name: tplName,
        description: tplDescription,
        steps: [],
      } as never);
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });
      setShowCreateTemplate(false);
      setTplName('');
      setTplDescription('');
    },
  });

  const startWorkflow = useMutation({
    mutationFn: async () => {
      const { error: insertError } = await supabase.from('workflow_instance').insert({
        title: newTitle,
        template_id: newTemplateId,
        due_date: newDueDate || null,
        status: 'todo',
      } as never);
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-instances'] });
      setShowStart(false);
      setNewTitle('');
      setNewTemplateId('');
      setNewDueDate('');
    },
  });

  const grouped = KANBAN_COLUMNS.map((col) => ({
    ...col,
    items: (instances ?? []).filter((inst) => inst.status === col.key),
  }));

  return (
    <div className="flex gap-6">
      <div className="flex-1 min-w-0">
        <PageHeader
          title="Workflows"
          description="Manage and track your workflows"
          action={
            <Button onClick={() => setShowStart(true)}>
              <Plus className="mr-2 h-4 w-4" /> Start Workflow
            </Button>
          }
        />

        {error && <ErrorAlert message={String(error)} onRetry={() => refetch()} />}

        <Tabs defaultValue="my-workflows">
          <TabsList>
            <TabsTrigger value="my-workflows">My Workflows</TabsTrigger>
            {isAdmin && <TabsTrigger value="templates">Templates</TabsTrigger>}
          </TabsList>

          <TabsContent value="my-workflows" className="mt-6">
            {isLoading && (
              <div className="grid grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-64 w-full" />
                ))}
              </div>
            )}

            {!isLoading && (instances ?? []).length === 0 && (
              <EmptyState
                icon={Workflow}
                title="No active workflows"
                description="Start a workflow from a template to get going."
                actionLabel="Start Workflow"
                onAction={() => setShowStart(true)}
              />
            )}

            {!isLoading && (instances ?? []).length > 0 && (
              <div className="grid grid-cols-4 gap-4">
                {grouped.map((col) => (
                  <div key={col.key} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">{col.label}</h3>
                      <Badge variant="secondary" className="text-xs">
                        {col.items.length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {col.items.map((inst) => (
                        <WorkflowCard
                          key={inst.id}
                          instance={inst}
                          onClick={() => setSelectedInstance(inst)}
                        />
                      ))}
                      {col.items.length === 0 && (
                        <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
                          No items
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="templates" className="mt-6">
              <div className="mb-4 flex justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowCreateTemplate(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Create Template
                </Button>
              </div>
              {templates && templates.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {templates.map((tpl) => (
                    <Card key={tpl.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{tpl.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">{tpl.description}</p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {tpl.step_count} steps
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No templates yet.</p>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Start Workflow Dialog */}
        <Dialog open={showStart} onOpenChange={setShowStart}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start Workflow</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Workflow title"
                />
              </div>
              <div className="space-y-2">
                <Label>Template</Label>
                <Select value={newTemplateId} onValueChange={setNewTemplateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {(templates ?? []).map((tpl) => (
                      <SelectItem key={tpl.id} value={tpl.id}>
                        {tpl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date (optional)</Label>
                <Input
                  type="date"
                  value={newDueDate}
                  onChange={(e) => setNewDueDate(e.target.value)}
                />
              </div>
              <Button
                onClick={() => startWorkflow.mutate()}
                disabled={!newTitle || !newTemplateId || startWorkflow.isPending}
                className="w-full"
              >
                {startWorkflow.isPending ? 'Starting...' : 'Start Workflow'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {selectedInstance && (
        <StepExecutionPanel
          instance={selectedInstance}
          onClose={() => setSelectedInstance(null)}
          onUpdated={() => {
            setSelectedInstance(null);
            refetch();
          }}
        />
      )}

      {/* Create Template Dialog */}
      <Dialog open={showCreateTemplate} onOpenChange={setShowCreateTemplate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={tplDescription} onChange={(e) => setTplDescription(e.target.value)} placeholder="Describe the workflow" />
            </div>
            <Button
              onClick={() => createTemplate.mutate()}
              disabled={!tplName || createTemplate.isPending}
              className="w-full"
            >
              {createTemplate.isPending ? 'Creating...' : 'Create Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
